import { X } from 'lucide-react';
import { cn } from '../../ui';
import type { ReportFilterDef } from './reportKit';
import { filterValueLabel, isSetValue } from './reportPresentation';

/**
 * What is actually narrowing this report, and how to stop it narrowing.
 *
 * The old page counted the filters ("3 set") and left the reader to open a panel to find out which
 * three — so a figure could be read as the company's number while a territory was quietly in force.
 * A chip names the filter AND its value in the reader's words, and removing one writes the same
 * URL-backed state the controls do.
 *
 * Only filters the reader actually set appear: a filter sitting on the value its control opens with
 * is not a narrowing, and printing "Territory: Any" as a chip would make the row noise.
 */
export function ActiveFilterChips({ filters, values, onRemove, onClear, className }: {
  /** The report's declared filters, for the label and the option list behind the value. */
  filters: ReportFilterDef[];
  /** Values as they were SET — defaults deliberately not folded in. */
  values: Record<string, any>;
  onRemove: (key: string) => void;
  onClear: () => void;
  className?: string;
}) {
  const active = filters.filter((f) => isSetValue(values[f.key]));
  if (!active.length) return null;

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5 py-1.5', className)}>
      <span className="type-caption text-subtle">Filtered by</span>
      {active.map((f) => (
        <span
          key={f.key}
          className="inline-flex max-w-[280px] items-center gap-1 rounded-pill border border-brand-500/35 bg-brand-500/[0.08] py-0.5 pl-2.5 pr-1 type-label text-content"
        >
          <span className="text-muted">{f.label}:</span>
          <span className="min-w-0 truncate" title={filterValueLabel(f, values[f.key])}>{filterValueLabel(f, values[f.key])}</span>
          <button
            type="button"
            onClick={() => onRemove(f.key)}
            aria-label={`Remove the ${f.label} filter`}
            className="grid size-4 shrink-0 place-items-center rounded-pill text-subtle transition-colors hover:bg-brand-500/20 hover:text-content"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClear}
        className="rounded px-1.5 py-0.5 type-label text-muted underline-offset-2 transition-colors hover:text-content hover:underline"
      >
        Clear all
      </button>
    </div>
  );
}
